/** App root: QueryClient, BrowserRouter, auth-gated routes */

import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuth } from './hooks/use-auth'
import { Layout } from './components/layout/layout'
import { AppShell } from './components/layout/app-shell'
import { LoginPage } from './routes/login'
import { DevLoginPage } from './routes/dev-login'
import { ProfilePage } from './routes/profile'
import { SettingsPage } from './routes/settings'
import { ApiDocsPage } from './routes/api-docs'
import { CliDocsPage } from './routes/cli-docs'
import { SearchAnalyticsPage } from './routes/search-analytics'
import { GraphPage } from './routes/graph'
import { ShareView } from './routes/share-view'
import { PublishedView } from './routes/published-view'
import { StoragePage } from './routes/storage'
import { HostedSitesPage } from './routes/hosted-sites'
import { PackagesPage } from './routes/packages'
import { PackageDetailPage } from './routes/packages.$id'
import { UploadPage } from './features/ingest/upload/UploadPage'
import { OcrSettingsAdmin } from './features/ingest/admin/OcrSettingsAdmin'
import { HomeDashboard } from './features/dashboard/home-dashboard'
import { TourProvider } from './features/onboarding-tour/tour-provider'
import { TourOverlay } from './features/onboarding-tour/tour-overlay'

const PreviewPage = lazy(() => import('./features/ingest/preview/PreviewPage'))
const LandingPage = lazy(() => import('./features/landing/LandingPage').then((m) => ({ default: m.LandingPage })))
const BatchReviewPage = lazy(() => import('./features/ingest/review/BatchReviewPage').then((m) => ({ default: m.BatchReviewPage })))
const DocumentViewPage = lazy(() => import('./features/document/DocumentView').then((m) => ({ default: m.DocumentView })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
})

/** Wraps a route that requires authentication */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-0">
        <div className="flex flex-col items-center gap-3">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-800 border-t-brand-500" />
          <span className="text-xs text-neutral-600">Loading...</span>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

/** Exported for testing — renders routes without BrowserRouter wrapper */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dev-login" element={<DevLoginPage />} />
      <Route path="/share/:token" element={<ShareView />} />
      <Route path="/pub/:slug" element={<PublishedView />} />
      <Route path="/profile" element={<RequireAuth><AppShell><ProfilePage /></AppShell></RequireAuth>} />
      <Route path="/settings" element={<RequireAuth><AppShell><SettingsPage /></AppShell></RequireAuth>} />
      <Route path="/docs/api" element={<RequireAuth><AppShell><ApiDocsPage /></AppShell></RequireAuth>} />
      <Route path="/docs/cli" element={<RequireAuth><AppShell><CliDocsPage /></AppShell></RequireAuth>} />
      <Route path="/settings/search-analytics" element={<RequireAuth><AppShell><SearchAnalyticsPage /></AppShell></RequireAuth>} />
      <Route path="/graph" element={<RequireAuth><AppShell><GraphPage /></AppShell></RequireAuth>} />
      <Route path="/storage" element={<RequireAuth><AppShell><StoragePage /></AppShell></RequireAuth>} />
      <Route path="/storage/:folderId" element={<RequireAuth><AppShell><StoragePage /></AppShell></RequireAuth>} />
      <Route path="/sites" element={<RequireAuth><AppShell><HostedSitesPage /></AppShell></RequireAuth>} />
      {/* GH-237: Onboarding packages — flag-gated; PackagesPage/PackageDetailPage return null or redirect when flag off */}
      <Route path="/packages" element={<RequireAuth><AppShell><PackagesPage /></AppShell></RequireAuth>} />
      <Route path="/packages/:id" element={<RequireAuth><AppShell><PackageDetailPage /></AppShell></RequireAuth>} />
      {/* Bare /ingest redirects to the canonical batch entry point so the
       *  natural URL discovers the pipeline. See #187. */}
      <Route path="/ingest" element={<Navigate to="/ingest/upload" replace />} />
      <Route path="/ingest/upload" element={<RequireAuth><AppShell><UploadPage /></AppShell></RequireAuth>} />
      <Route
        path="/ingest/batches/:batchId/review"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-800 border-t-brand-500" /></div>}>
                <BatchReviewPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/ingest/batches/:batchId/items/:itemId"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-800 border-t-brand-500" /></div>}>
                <PreviewPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />
      <Route path="/admin/ocr-settings" element={<RequireAuth><AppShell><OcrSettingsAdmin /></AppShell></RequireAuth>} />
      <Route
        path="/document/:id"
        element={
          <RequireAuth>
            <Suspense fallback={<div className="flex h-screen items-center justify-center bg-surface-0"><div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-800 border-t-brand-500" /></div>}>
              <DocumentViewPage />
            </Suspense>
          </RequireAuth>
        }
      />
      {/* GH-251: Home dashboard — replaces blank editor shell at / */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell>
              <HomeDashboard />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/doc/:slug"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      />
      {/* Public marketing route — flag-gated (VITE_ENABLE_INGEST_LANDING). LandingPage returns null when flag is off. */}
      <Route
        path="/landing/ingest"
        element={
          <Suspense fallback={null}>
            <LandingPage />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TourProvider>
          <AppRoutes />
          <TourOverlay />
        </TourProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
